/**
 * Qualifications API Client (Epic C-2 — Kwalificaties)
 *
 * AD-9: `lib/<resource>Api.ts`-conventie, zelfde patroon als
 * `planningApi.ts`. Story C-2.1 levert de lijst-CRUD-calls
 * (`/api/v1/qualifications/{product-categories,target-markets}`); Story
 * C-2.2 breidt dit bestand uit met de matrix-/toggle-calls.
 */

import { apiRequest } from './api';
import type {
  CreateQualificationListItemInput,
  EmployeeQualificationDTO,
  GrantQualificationInput,
  PlanningTaskActivity,
  QualificationDryRunResultDTO,
  QualificationListItemDTO,
  QualificationScopedEmployeeDTO,
  QualificationWhoCanEntryDTO,
  UpdateQualificationListItemInput,
} from '@functieloonhuis/shared';

/** URL-padsegment — NIET hetzelfde als de backend-interne `kind`-waarde (`productCategory`/`targetMarket`). */
export type QualificationListKind = 'product-categories' | 'target-markets';

interface DataResponse<T> {
  data: T;
}

/** Story C-2.2 — matrix/toggle-calls. */
export interface RevokeQualificationInput {
  employeeId: number;
  activity: PlanningTaskActivity;
  productCategoryId: number;
  /** Afwezig bij een activiteit zonder doelmarkt — zie `activityUsesTargetMarket`. */
  targetMarketId?: number | null;
}

/**
 * Bouwt de identificerende querystring voor revoke/dryRun.
 *
 * De doelmarkt wordt WEGGELATEN als hij er niet is, niet als lege of
 * ge-`String()`-te waarde meegestuurd: `String(undefined)` levert letterlijk
 * `targetMarketId=undefined` op, waar de server `z.coerce.number()` op loslaat
 * — NaN, en dus een 400 op een verzoek dat gewoon geldig is.
 */
function revokeParams(input: RevokeQualificationInput): URLSearchParams {
  const params = new URLSearchParams({
    employeeId: String(input.employeeId),
    activity: input.activity,
    productCategoryId: String(input.productCategoryId),
  });
  // Ook 0 telt als afwezig: dat is de sleutelwaarde voor "geen doelmarkt"
  // (`targetMarketKey`), en als échte id bestaat hij niet. Zou hij hier als
  // `targetMarketId=0` de deur uit gaan, dan wijst de server hem af op de
  // positiviteitscontrole en ziet de gebruiker alleen "ophalen mislukt".
  if (
    input.targetMarketId !== undefined &&
    input.targetMarketId !== null &&
    input.targetMarketId > 0
  ) {
    params.set('targetMarketId', String(input.targetMarketId));
  }
  return params;
}

export interface WhoCanDoWhatFilters {
  activity: PlanningTaskActivity;
  productCategoryId?: number;
  targetMarketId?: number;
}

export interface ScopedEmployeesFilters {
  departmentId?: number;
  search?: string;
}

export const qualificationsApi = {
  listItems: async (
    kind: QualificationListKind,
    includeArchived = false
  ): Promise<QualificationListItemDTO[]> => {
    const query = includeArchived ? '?includeArchived=true' : '';
    const res = await apiRequest<DataResponse<QualificationListItemDTO[]>>(
      `/qualifications/${kind}${query}`
    );
    return res.data;
  },

  createItem: async (
    kind: QualificationListKind,
    data: CreateQualificationListItemInput
  ): Promise<QualificationListItemDTO> => {
    const res = await apiRequest<DataResponse<QualificationListItemDTO>>(
      `/qualifications/${kind}`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return res.data;
  },

  updateItem: async (
    kind: QualificationListKind,
    id: number,
    data: UpdateQualificationListItemInput
  ): Promise<QualificationListItemDTO> => {
    const res = await apiRequest<DataResponse<QualificationListItemDTO>>(
      `/qualifications/${kind}/${id}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    );
    return res.data;
  },

  archiveItem: async (
    kind: QualificationListKind,
    id: number
  ): Promise<QualificationListItemDTO> => {
    const res = await apiRequest<DataResponse<QualificationListItemDTO>>(
      `/qualifications/${kind}/${id}/archive`,
      {
        method: 'POST',
      }
    );
    return res.data;
  },

  restoreItem: async (
    kind: QualificationListKind,
    id: number
  ): Promise<QualificationListItemDTO> => {
    const res = await apiRequest<DataResponse<QualificationListItemDTO>>(
      `/qualifications/${kind}/${id}/restore`,
      {
        method: 'POST',
      }
    );
    return res.data;
  },

  scopedEmployees: async (
    filters: ScopedEmployeesFilters = {}
  ): Promise<QualificationScopedEmployeeDTO[]> => {
    const params = new URLSearchParams();
    if (filters.departmentId !== undefined)
      params.set('departmentId', String(filters.departmentId));
    if (filters.search) params.set('search', filters.search);
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await apiRequest<DataResponse<QualificationScopedEmployeeDTO[]>>(
      `/qualifications/employees${query}`
    );
    return res.data;
  },

  employeeMatrix: async (employeeId: number): Promise<EmployeeQualificationDTO[]> => {
    const res = await apiRequest<DataResponse<EmployeeQualificationDTO[]>>(
      `/qualifications/employees/${employeeId}`
    );
    return res.data;
  },

  whoCanDoWhat: async (filters: WhoCanDoWhatFilters): Promise<QualificationWhoCanEntryDTO[]> => {
    const params = new URLSearchParams({ activity: filters.activity });
    if (filters.productCategoryId !== undefined)
      params.set('productCategoryId', String(filters.productCategoryId));
    if (filters.targetMarketId !== undefined)
      params.set('targetMarketId', String(filters.targetMarketId));
    const res = await apiRequest<DataResponse<QualificationWhoCanEntryDTO[]>>(
      `/qualifications?${params.toString()}`
    );
    return res.data;
  },

  grant: async (input: GrantQualificationInput): Promise<EmployeeQualificationDTO> => {
    const res = await apiRequest<DataResponse<EmployeeQualificationDTO>>('/qualifications', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return res.data;
  },

  /** `dryRun=true` — leesvraag (AD-15), geen mutatie. */
  dryRunRevoke: async (input: RevokeQualificationInput): Promise<QualificationDryRunResultDTO> => {
    const params = revokeParams(input);
    params.set('dryRun', 'true');
    const res = await apiRequest<DataResponse<QualificationDryRunResultDTO>>(
      `/qualifications?${params.toString()}`,
      {
        method: 'DELETE',
      }
    );
    return res.data;
  },

  revoke: async (input: RevokeQualificationInput): Promise<{ deleted: boolean }> => {
    const params = revokeParams(input);
    const res = await apiRequest<DataResponse<{ deleted: boolean }>>(
      `/qualifications?${params.toString()}`,
      {
        method: 'DELETE',
      }
    );
    return res.data;
  },
};
